import { useState } from "react";
import { Link } from "react-router-dom";

import "./pages.css";
import { Empty } from "../components/Empty";
import { LotsTable } from "../components/LotsTable";
import { ModePill } from "../components/ModePill";
import { Panel } from "../components/Panel";
import { TitleBand } from "../components/TitleBand";
import { Variation } from "../components/Variation";
import { adena, day, signed } from "../format";
import { itemHref } from "../search/nav";
import { useTrader } from "./Trader";
import type { TraderSort } from "./traderSort";
import { filterTraderItems, listingKey, moved, sortTraderItems, vsMarket } from "./traderSort";

const SORTS: TraderSort[] = ["item", "mode", "vsMarket", "moved"];
const SORT_LABELS: Record<TraderSort, string> = {
  item: "item", mode: "mode", vsMarket: "vs market", moved: "moved",
};

/** Buy and sell keep their hue; craft takes none. tokens.css spends rose and
 *  olive on the WTS/WTB pair, and a fee is neither of them. */
function hue(mode: string): string {
  return mode === "buy" || mode === "sell" ? mode : "";
}

export function TraderNow() {
  const trader = useTrader();
  const [sort, setSort] = useState<TraderSort>("item");
  const [filter, setFilter] = useState("");

  const rows = sortTraderItems(filterTraderItems(trader.items, filter), sort);
  const needle = filter.trim();

  return (
    <>
      <Panel primary>
        <div className="strip">
          <span>Sort</span>
          {SORTS.map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={sort === option}
              onClick={() => setSort(option)}
            >
              {SORT_LABELS[option]}
            </button>
          ))}
          <span style={{ flex: 1 }} />
          <input
            aria-label="Filter items"
            placeholder="filter item…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>

        {rows.length === 0 ? (
          <Empty>
            {needle ? (
              `Nothing matching “${needle}”.`
            ) : (
              <>
                Nothing was out when they were last walked.{" "}
                <Link to="history">Their history</Link> has what they used to offer.
              </>
            )}
          </Empty>
        ) : (
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Mode</th>
                  <th className="num">Theirs</th>
                  <th className="num">Market</th>
                  <th className="num">vs market</th>
                  <th className="num">Was</th>
                  <th className="num">Moved</th>
                  <th>Seen</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  // listingKey, not `${item_id}-${mode}`: a trader with a +0
                  // and a +6 of one item is two rows, and the old key rendered
                  // both under one.
                  <tr key={listingKey(row)}>
                    <td>
                      <Link to={itemHref(row.item_id, row.item)}>{row.item}</Link>
                      <Variation {...row} />
                    </td>
                    <td><ModePill mode={row.mode} /></td>
                    <td className={`num ${hue(row.mode)}`}>{adena(row.theirs)}</td>
                    <td className="num">{adena(row.market_best)}</td>
                    <td
                      className={`num ${vsMarket(row) === null ? "dim" : ""}`}
                      title="Against the best anyone managed in the same scan. Zero means this trader set it."
                    >
                      {signed(vsMarket(row))}
                    </td>
                    <td className="num dim">{adena(row.previous)}</td>
                    <td className={`num ${moved(row) === null ? "dim" : ""}`}>
                      {signed(moved(row))}
                    </td>
                    <td className="dim">{day(row.observed_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {trader.lots.length > 0 ? (
        <>
          <TitleBand
            title="Package sales"
            note={`${trader.lots.length} ${trader.lots.length === 1 ? "lot" : "lots"} out last walk, sold whole`}
          />
          <Panel><LotsTable lots={trader.lots} showTrader={false} /></Panel>
        </>
      ) : null}
    </>
  );
}
